// One dancer's schedule as a PDF: the client half of the export built in PR #112.
//
// The request lives in its own module rather than inside the Students tab so the
// three rules that actually matter can be unit tested without rendering the tab
// and without touching the DOM.
//
//  1. The studio is NEVER put in the query string. GET /schedules/export-dancer
//     takes the studio from the signed-in token, which is what stops one studio
//     asking for another studio's dancer. Adding a userId parameter back would
//     quietly reopen the hole PR #113 closed on the other four export routes, so
//     a test asserts the query carries only boardId and studentId.
//  2. The saved file is named by the server when the server says so, through its
//     own Content-Disposition header, so the file a family receives carries the
//     same name the PDF was built with. The dancer's name is only a fallback.
//  3. The studio owner reads the server's message, not ours. "Ava Johnson is not
//     in any class on this schedule yet" is an answer she can act on; "Export
//     failed" is not.

// Same resolution order as RequestService and ExportPanel.
export const apiBaseUrl = () =>
    (window._env_ && window._env_.REACT_APP_API_URL) ||
    process.env.REACT_APP_API_URL ||
    'http://localhost:8080';

export const GENERIC_EXPORT_ERROR = 'The schedule could not be exported. Please try again.';

// Mirrors dancerExportFilename in server/src/utils/dancerExportHelpers.js, and is
// used only when the response carries no Content-Disposition of its own.
export const dancerExportFilename = (dancerName) => {
    const safe = String(dancerName || '')
        .trim()
        .replace(/[^A-Za-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    return `${safe || 'dancer'}-schedule.pdf`;
};

export const filenameFromDisposition = (header) => {
    if (!header) return null;
    const value = String(header);

    const encoded = /filename\*=UTF-8''([^;]+)/i.exec(value);
    if (encoded) {
        try {
            const decoded = decodeURIComponent(encoded[1].trim());
            if (decoded) return decoded;
        } catch (e) {
            // A malformed percent-escape is not worth failing a download over.
        }
    }

    const quoted = /filename="([^"]*)"/i.exec(value);
    if (quoted) return quoted[1].trim() || null;

    const bare = /filename=([^;]+)/i.exec(value);
    if (bare) return bare[1].trim().replace(/^"|"$/g, '') || null;

    return null;
};

// The export routes answer with a blob, so an error body is a Blob of JSON rather
// than a parsed object. Read it back out so the studio owner sees what the server
// actually said.
export const exportErrorMessage = async (err) => {
    const body = err && err.response && err.response.data;
    if (!body) return GENERIC_EXPORT_ERROR;
    try {
        let text;
        if (typeof body.text === 'function') {
            text = await body.text();
        } else if (typeof body === 'string') {
            text = body;
        } else {
            text = JSON.stringify(body);
        }
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed.message === 'string' && parsed.message.trim()) {
            return parsed.message;
        }
    } catch (e) {
        // Body was not JSON. Nothing useful to show, so say the plain thing.
    }
    return GENERIC_EXPORT_ERROR;
};

export const dancerExportUrl = (baseUrl, { boardId, studentId }) => {
    const base = String(baseUrl || apiBaseUrl()).replace(/\/$/, '');
    const params = new URLSearchParams([
        ['boardId', String(boardId)],
        ['studentId', String(studentId)],
    ]);
    return `${base}/schedules/export-dancer?${params.toString()}`;
};

/**
 * Ask the server for one dancer's week as a PDF.
 *
 * Returns { blob, filename }. Throws an Error whose message is already fit to
 * show a studio owner, so the caller never has to decide what to say.
 */
export const fetchDancerSchedulePdf = async ({
    httpGet,
    baseUrl,
    boardId,
    studentId,
    dancerName,
    accessToken,
}) => {
    if (!boardId || !studentId) {
        throw new Error('Pick a schedule and a dancer before exporting.');
    }

    let response;
    try {
        response = await httpGet(dancerExportUrl(baseUrl, { boardId, studentId }), {
            responseType: 'blob',
            headers: { Authorization: 'Bearer ' + (accessToken || '') },
        });
    } catch (err) {
        throw new Error(await exportErrorMessage(err));
    }

    const headers = (response && response.headers) || {};
    const disposition = headers['content-disposition'] || headers['Content-Disposition'];

    return {
        blob: new Blob([response && response.data], { type: 'application/pdf' }),
        filename: filenameFromDisposition(disposition) || dancerExportFilename(dancerName),
    };
};
